// NOTE:
// virtual-window-page（home以外）でのvirtual-windowのバーコントロールの制御をここで行う。
// カスタムブロック内で行いたかったが、virtual-window-pageがarchiveページなどにも適用されるため、
// カスタムブロックで実装しないようにしたため、ここで制御する。

type Elements = {
  virtualWindow: HTMLDivElement;
  menuButton: HTMLButtonElement;
  closeButton: HTMLButtonElement | null;
  shrinkButton: HTMLButtonElement | null;
  expandButton: HTMLButtonElement | null;
};

export class VirtualWindowController {
  #elements: Elements | null = null;
  #mutationObserver: MutationObserver | null = null;

  constructor() {}

  init() {
    const elements = this.#findElements();
    if (!elements) {
      return;
    }
    this.#elements = elements;
    this.#addEventListeners(elements);
    this.#initMutationObserver(elements);
  }

  #findElements(): Elements | null {
    const virtualWindowPages = document.getElementById(
      'virtual-window-page-template'
    );

    if (!virtualWindowPages) {
      return null;
    }

    const virtualWindow =
      virtualWindowPages.querySelector<HTMLDivElement>('.virtual-window');
    if (!virtualWindow) {
      return null;
    }

    const menuButton =
      virtualWindow.querySelector<HTMLButtonElement>('.menu-button');

    if (!menuButton) {
      return null;
    }
    const bar = virtualWindow.querySelector('.bar');
    const closeButton =
      bar?.querySelector<HTMLButtonElement>('.close-button') ?? null;
    const shrinkButton =
      bar?.querySelector<HTMLButtonElement>('.shrink-button') ?? null;
    const expandButton =
      bar?.querySelector<HTMLButtonElement>('.expand-button') ?? null;
    return {
      virtualWindow,
      menuButton,
      closeButton,
      shrinkButton,
      expandButton,
    };
  }

  #addEventListeners(elements: Elements) {
    elements.menuButton.addEventListener('click', this.#handleMenuButtonClick);
    elements.closeButton?.addEventListener(
      'click',
      this.#handleCloseButtonClick
    );
    elements.shrinkButton?.addEventListener(
      'click',
      this.#handleShrinkButtonClick
    );
    elements.expandButton?.addEventListener(
      'click',
      this.#handleExpandButtonClick
    );
  }

  #handleMenuButtonClick = () => {
    this.#elements?.virtualWindow.classList.toggle('expanded');
  };

  #handleCloseButtonClick = () => {
    this.#elements?.closeButton?.classList.toggle('toggled');
    this.#elements?.virtualWindow.classList.toggle('closed');
  };

  #handleShrinkButtonClick = () => {
    this.#elements?.virtualWindow.classList.remove('expanded');
  };

  #handleExpandButtonClick = () => {
    this.#elements?.virtualWindow.classList.add('expanded');
  };

  #initMutationObserver(elements: Elements) {
    const hasExpandedClass = (className: string | null) => {
      return (className ?? '').split(/\s+/).includes('expanded');
    };

    const onExpandedChange = (isExpanded: boolean) => {
      if (isExpanded) {
        elements.menuButton.classList.remove('toggled');
        elements.menuButton.innerText = 'menu';
      } else {
        elements.menuButton.classList.add('toggled');
        elements.menuButton.innerText = 'close';
      }

      elements.virtualWindow?.addEventListener(
        'transitionend',
        () => {
          elements.virtualWindow?.classList.remove('width-transitioning');
        },
        { once: true }
      );
      elements.virtualWindow?.classList.add('width-transitioning');
    };

    this.#mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type !== 'attributes' ||
          mutation.attributeName !== 'class'
        ) {
          continue;
        }

        const wasExpanded = hasExpandedClass(mutation.oldValue);
        const isExpanded =
          elements.virtualWindow?.classList.contains('expanded') ?? false;

        if (wasExpanded === isExpanded) {
          continue;
        }

        onExpandedChange(isExpanded);
      }
    });

    this.#mutationObserver?.observe(elements.virtualWindow, {
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: true,
    });
  }

  dispose() {
    this.#elements?.menuButton?.removeEventListener(
      'click',
      this.#handleMenuButtonClick
    );
    this.#elements?.closeButton?.removeEventListener(
      'click',
      this.#handleCloseButtonClick
    );
    this.#elements?.shrinkButton?.removeEventListener(
      'click',
      this.#handleShrinkButtonClick
    );
    this.#elements?.expandButton?.removeEventListener(
      'click',
      this.#handleExpandButtonClick
    );
    this.#mutationObserver?.disconnect();
    this.#mutationObserver = null;
  }
}
